import { HttpParams } from '@angular/common/http';
import { Component, forwardRef, Inject, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import * as _ from 'lodash';
import * as moment_tz from 'moment-timezone';
import { take } from "rxjs";
import { LOCALE_TIMEZONE } from '../../common/config';
import { ErrorMessages, Permissions } from '../../common/constants';
import { DataExportConfigurationProperties, DataExportState, DataExportStatus, DataExportTimestampFormat, Tenant, Thing, ThingInventoryManagementType } from '../../model/index';
import { AuthenticationService } from '../../service/authentication.service';
import { DataExportConfigurationService } from '../../service/data-export-configuration.service';
import { AbstractContextService } from '../../shared/class/abstract-context-service.class';
import { AbstractThingContextService } from '../../shared/class/abstract-thing-context-service.class';
import { CustomTablePage } from '../../shared/class/custom-table-page.class';
import { ConfirmDialog } from '../../shared/confirm-dialog/confirm-dialog.component';
import { ButtonActionValue, CustomTableService } from '../../shared/custom-table';
import { DownloadProgressBarDialogComponent } from '../../shared/download-progress-bar-dialog/download-progress-bar-dialog';
import { DatetimeFormatterPipe, DurationFormatterPipe, LocalizationPipe } from '../../shared/pipe';
import { ErrorUtility } from '../../utility/error-utility';
import { DataExportWidgetService } from './data-export-widget.service';

@Component({
    selector: 'data-export-widget',
    template: require('./data-export-widget.component.html'),
    styles: [require('./data-export-widget.component.css')],
    providers: [DataExportWidgetService]
})
export class DataExportWidgetComponent extends CustomTablePage<DataExportStatus> implements OnInit, OnDestroy {

    @Input() title: string = "dataExportProperty";

    @Input() description: string;

    error: string;
    loaded: boolean;
    showScheduleDataExport: boolean = false;
    exportData: DataExportStatus[] = [];
    writePermission: boolean;
    configuration: DataExportConfigurationProperties;
    showSuggestedExportButtons: boolean;
    suggestedExportButtons: SuggestedExportButton[];
    suggestedExportButtonsLoaded: boolean;
    expiringDataTooltip: string = 'The saved data related to this period will be deleted soon. You have time until the end of the month to download and save it.';
    suggestedExportButtonsDisabled: boolean;
    expiringElementButton: SuggestedExportButton;

    // filler loading properties
    fillerRowCount: number = 10;
    fillerRowHeight: number = 10;
    fillerRowHeightWithSpace: number = 12;
    fillerRowOffset: number = 10;

    private refreshIntervalMillis = 5000;
    private intervalId: any;
    private timezone: string;
    private language: string;
    private rawDataRetentionMonths: number;
    private currentThing: Thing;
    private tenant: Tenant;

    constructor(
        @Inject(forwardRef(() => DataExportWidgetService)) private dataExportWidgetService: DataExportWidgetService,
        @Inject(forwardRef(() => NgZone)) private zone: NgZone,
        @Inject(forwardRef(() => AuthenticationService)) private authenticationService: AuthenticationService,
        @Inject(forwardRef(() => MatDialog)) private dialog: MatDialog,
        @Inject(forwardRef(() => LocalizationPipe)) private localizationPipe: LocalizationPipe,
        @Inject(forwardRef(() => DurationFormatterPipe)) private durationFormatterPipe: DurationFormatterPipe,
        @Inject(forwardRef(() => DataExportConfigurationService)) private dataExportConfigurationService: DataExportConfigurationService,
        @Inject(forwardRef(() => AbstractThingContextService)) private thingContextService: AbstractThingContextService,
        @Inject(forwardRef(() => DatetimeFormatterPipe)) private datetimeFormatterPipe: DatetimeFormatterPipe,
        @Inject(forwardRef(() => AbstractContextService)) private contextService: AbstractContextService
    ) {
        super();
    }

    ngOnInit(): void {
        this.tenant = this.authenticationService.getTenant();
        const thingInventoryManagement = this.authenticationService.getThingInventoryManagement();
        this.sort = ['dateTime', 'desc'];
        this.currentThing = this.thingContextService.getCurrentThing();
        this.writePermission = (thingInventoryManagement != ThingInventoryManagementType.BY_MODEL || !this.currentThing?.productModelPartId) && this.authenticationService.hasPermission(Permissions.EXPORT_DATA);
        if (this.writePermission) {
            this.dataExportConfigurationService.getConfiguration().then(configuration => {
                this.configuration = configuration;
                this.setDisplayedColumns();
                this.loadData();
                this.startRefreshing();
            }).catch(err => this.error = ErrorUtility.getMessage(err, ErrorMessages.GET_DATA_ERROR));
        }
        this.timezone = this.authenticationService.getUser()?.timezone;
        this.language = this.authenticationService.getUser()?.locale || this.authenticationService.getUser()?.language || navigator.language;
        this.rawDataRetentionMonths = this.authenticationService.getTenant().rawDataRetentionMonths;
        this.expiringDataTooltip = this.localizationPipe.transform(this.expiringDataTooltip);
        this.showSuggestedExportButtons = this.writePermission && (!!this.contextService.getCurrentCustomer() || !!this.authenticationService.isCustomerUser()) && this.rawDataRetentionMonths > 0;
        this.loadSuggestedExportButtons();
    }

    ngOnDestroy(): void {
        this.stopRefreshing();
    }

    private startRefreshing(): void {
        this.zone.runOutsideAngular(() => {
            this.intervalId = setInterval(() => {
                this.zone.run(() => this.loadData())
            }, this.refreshIntervalMillis)
        });
    }

    private stopRefreshing(): void {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
    }

    loadData(): Promise<void> {
        return this.dataExportWidgetService.getPagedDataExportStatuses(this.sort, this.pageIndex, this.pageSize, this.searchText).then(pagedList => {
            if (!_.isEqual(this.exportData, pagedList.content)) {
                this.exportData = pagedList.content;
                this.enrichContent(pagedList.content);
                this.length = pagedList.totalElements;
                this.pageIndex = pagedList.number;
                this.pageSize = pagedList.size;
                this.dataSource = new MatTableDataSource<DataExportStatus>(pagedList.content);
            }
            this.loaded = true;
            this.error = null;
        }).catch(err => {
            this.error = ErrorUtility.getMessage(err, ErrorMessages.GET_DATA_ERROR);
        });
    }

    downloadFile(dataExportId: string) {
        this.stopRefreshing();
        const dialogRef = this.openDownloadDialog();
        this.dataExportWidgetService.getExportBulkDataById(dataExportId).then(res => {
            this.dataExportWidgetService.downloadFromExternalUrl(res.url, res.dateTime);
            dialogRef.close();
            this.loadData();
            this.startRefreshing();
        }).catch(err => {
            this.error = ErrorUtility.getMessage(err, ErrorMessages.GET_DATA_ERROR);
            dialogRef.close();
        });
    }

    openScheduleForm(): void {
        this.stopRefreshing();
        this.showScheduleDataExport = true;
    }

    closeScheduleForm(): void {
        this.showScheduleDataExport = false;
        this.loadData();
        this.loadSuggestedExportButtons();
        this.startRefreshing();
    }

    private openDownloadDialog(): any {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '10%';
        dialogConfig.disableClose = true;
        dialogConfig.panelClass = "download-progress-bar-dialog";
        dialogConfig.data = { title: "downloadsTitle" };
        return this.dialog.open(DownloadProgressBarDialogComponent, dialogConfig);
    }

    protected setDisplayedColumns(): void {
        this.displayedColumns = [
            CustomTableService.newSimpleColumn('name', 'nameProperty', 'name').withSortField('name'),
            CustomTableService.newSimpleColumn('dateTime', 'creationDateProperty', 'printedDateTime').withSortField('dateTime'),
            CustomTableService.newSimpleColumn('availableUntilDate', 'availableUntilProperty', 'availableUntilDate'),
            CustomTableService.newPipedColumn('status', 'statusProperty', 'status', 'dataExportState').withSortField('state'),
            CustomTableService.newButtonColumn('downloadDataExport', '', 'id', 'float-right', 'downloadButton').withMatIcon('download').withMatIconClass('material-symbols-outlined').withVisiblePath('showDownload').withStyle({ '_any': { 'font-size': '20px', 'width': '10px' } }).withStickyEndColumn().withColumnClass('no-print'),
            CustomTableService.newButtonColumn('deleteDataExport', '', 'id', 'float-right', 'deleteButton').withMatIcon('delete').withMatIconClass('material-symbols-outlined').withVisiblePath('showDelete').withStyle({ '_any': { 'font-size': '20px', 'color': '#ff0000', 'width': '10px' } }).withStickyEndColumn().withColumnClass('no-print')
        ];
    }

    private enrichContent(elements: DataExportStatus[]): any {
        elements.forEach(el => {
            el['showDownload'] = el.state == DataExportState.COMPLETED ? true : null;
            el['showDelete'] = el.state != DataExportState.PENDING ? true : null;
            el['status'] = this.printStatus(el);
            const dateTimeMoment = moment_tz.tz(el.dateTime.toString(), 'YYYYMMDDHHmmss', this.timezone);
            el['printedDateTime'] = this.datetimeFormatterPipe.transform(dateTimeMoment.valueOf(), 'YYYY-MM-DD HH:mm', this.timezone, this.language);
            const availableUntilMoment = dateTimeMoment.add(30, 'days');
            el['availableUntilDate'] = this.datetimeFormatterPipe.transform(availableUntilMoment.valueOf(), 'YYYY-MM-DD', this.timezone, this.language);
        });
        return elements;
    }

    private printStatus(dataExport: DataExportStatus): string {
        if (dataExport.state == DataExportState.PENDING && dataExport.totalCount != null && dataExport.exportedCount != null) {
            const ofTranslation = this.localizationPipe.transform("of");
            const suffix = this.localizationPipe.transform("Things processed");
            let timeRemaining = "";
            if (dataExport.remainingSeconds != null) {
                const prefix = " (" + this.localizationPipe.transform("remaining time") + ": ";
                const time = this.durationFormatterPipe.transform({ startTimestamp: 0, endTimestamp: dataExport.remainingSeconds * 1000 });
                timeRemaining = prefix + time + ")";
            }
            return dataExport.exportedCount + " " + ofTranslation + " " + dataExport.totalCount + " " + suffix + timeRemaining
        } else {
            return dataExport.state;
        }
    }

    execButtonAction(actionValue: ButtonActionValue): void {
        switch (actionValue.action) {
            case 'downloadDataExport':
                this.downloadFile(actionValue.value);
                break;
            case 'deleteDataExport':
                this.openDeleteDialog(actionValue.value);
                break;
        }
    }

    private openDeleteDialog(id: string): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '25%';
        dialogConfig.panelClass = "data-export-widget-delete-dialog";
        dialogConfig.autoFocus = false;
        dialogConfig.data = {
            title: "dataExportDeleteMessage",
            message: "dataExportDeleteInfoMessage"
        }
        this.dialog.open(ConfirmDialog, dialogConfig).afterClosed().pipe(take(1)).subscribe(result => {
            if (result) {
                this.dataExportWidgetService.deleteDataExportStatus(id).then(() => {
                    this.loadData();
                    this.loadSuggestedExportButtons();
                }).catch(err => this.error = ErrorUtility.getMessage(err, ErrorMessages.GET_DATA_ERROR));
            }
        });
    }

    private loadSuggestedExportButtons(): void {
        if (!this.showSuggestedExportButtons) {
            return;
        }
        this.suggestedExportButtonsLoaded = false;
        this.suggestedExportButtons = [];
        this.dataExportWidgetService.getRecursivelyAllDataExportStatus().then(status => {
            const dataExportStatusNames: string[] = status.map(s => { return s.name });
            if (this.rawDataRetentionMonths > 12) {
                for (let i = 1; i <= 6; i++) {
                    const suggestedButton = this.getSuggestedExportButton(i, dataExportStatusNames);
                    if (suggestedButton != null) {
                        this.suggestedExportButtons.push(suggestedButton);
                    }
                }
                for (let i = (this.rawDataRetentionMonths - 6); i <= (this.rawDataRetentionMonths - 2); i++) {
                    const suggestedButton = this.getSuggestedExportButton(i, dataExportStatusNames);
                    if (suggestedButton != null) {
                        this.suggestedExportButtons.push(suggestedButton);
                    }
                }
            } else {
                for (let i = 1; i <= (this.rawDataRetentionMonths - 2); i++) {
                    const suggestedButton = this.getSuggestedExportButton(i, dataExportStatusNames);
                    if (suggestedButton != null) {
                        this.suggestedExportButtons.push(suggestedButton);
                    }
                }
            }
            this.expiringElementButton = this.getSuggestedExportButton((this.rawDataRetentionMonths - 1), dataExportStatusNames);
            if (this.expiringElementButton != null || this.suggestedExportButtons.length) {
                this.suggestedExportButtonsLoaded = true;
            } else {
                this.showSuggestedExportButtons = false;
            }
        });
    }

    private getSuggestedExportButton(monthIndex: number, dataExportStatusNames: string[]): SuggestedExportButton {
        const startTimestamp = moment_tz.tz(this.timezone || LOCALE_TIMEZONE).subtract(monthIndex, 'months').startOf('month').valueOf();
        const endTimestamp = moment_tz.tz(this.timezone || LOCALE_TIMEZONE).subtract(monthIndex, 'months').endOf('month').valueOf();
        const label = this.datetimeFormatterPipe.transform(startTimestamp, 'MMM YYYY', this.timezone, this.language);
        const name = this.dataExportWidgetService.getDefaultExportName(this.datetimeFormatterPipe.transform(startTimestamp, "MMM_YYYY", this.timezone, this.language), false);
        if (this.tenant.creationDate == null || endTimestamp > this.tenant.creationDate) {
            return { startTimestamp: startTimestamp, endTimestamp: endTimestamp, label: label, name: name, disabled: dataExportStatusNames.includes(name) };
        } else {
            return null;
        }
    }

    performSuggestedExportButton(index: number): void {
        this.performSuggestedExport(this.suggestedExportButtons[index]);
    }

    performSuggestedExpiringElementExportButton(): void {
        this.performSuggestedExport(this.expiringElementButton);
    }

    private performSuggestedExport(button: SuggestedExportButton): void {
        this.suggestedExportButtonsDisabled = true;
        let params = new HttpParams();
        let body = {
            name: button.name,
            thingFileName: this.dataExportWidgetService.getDefaultFileName(),
            startTimestamp: button.startTimestamp,
            endTimestamp: button.endTimestamp,
            timestampFormat: DataExportTimestampFormat.ISO_8601,
            includeUnit: true
        }
        if (this.thingContextService.getCurrentThing()) {
            params = params.set('selectedThingId', this.thingContextService.getCurrentThing().id);
            body['thingId'] = this.thingContextService.getCurrentThing().id;
        } else if (this.contextService.getCurrentLocation()) {
            params = params.set('locationId', this.contextService.getCurrentLocation().id);
            body['locationId'] = this.contextService.getCurrentLocation().id;
        } else if (this.contextService.getCurrentCustomer()) {
            params = params.set('customerId', this.contextService.getCurrentCustomer().id);
            body['customerId'] = this.contextService.getCurrentCustomer().id;
        } else if (this.contextService.getCurrentPartner()) {
            params = params.set('partnerId', this.contextService.getCurrentPartner().id);
            body['partnerId'] = this.contextService.getCurrentPartner().id;
        }
        this.dataExportWidgetService.getExportAllQualifiedMetricIds(params).then(qualifiedMetricIds => {
            body['qualifiedMetricIds'] = qualifiedMetricIds;
            this.dataExportWidgetService.scheduleBulkDataExport(body).then(() => {
                this.dataExportWidgetService.showSnackbar("dataExportScheduledProperty");
                this.suggestedExportButtonsDisabled = false;
                this.loadData();
                button.disabled = true;
            }).catch(err => {
                this.dataExportWidgetService.showSnackbar(ErrorUtility.getMessage(err, ErrorMessages.SAVE_DATA_ERROR), true);
                this.suggestedExportButtonsDisabled = false;
            });
        }).catch(err => {
            this.dataExportWidgetService.showSnackbar(ErrorUtility.getMessage(err, ErrorMessages.SAVE_DATA_ERROR), true);
            this.suggestedExportButtonsDisabled = false;
        });
    }
}

class SuggestedExportButton {
    startTimestamp: number;
    endTimestamp: number;
    label: string;
    name: string;
    disabled: boolean;
}